import type {Client} from "@microsoft/microsoft-graph-client";

import {Errors, KAError} from "@khan/logging";

import {
    MISSING_LICENSE_MESSAGE,
    ONE_DRIVE_ROOT_FOLDER,
    USER_ONE_DRIVE_BASE,
} from "./constants.ts";
import {safeMicrosoftFileName} from "./internal/safe-file-name.ts";
import {
    isGraphAPIError,
    isListChildrenResponse,
    isMissingLicenseError,
    isOneDriveItem,
    type ListChildrenResponse,
    type OneDriveItem,
} from "./types.ts";

/**
 * Saves a document to Microsoft OneDrive.  When a file with the desired name
 * already exists, a number is appended to the desired name.
 *
 * @param graphClient Microsoft Graph Client that performs the request.
 * @param fileName Desired file name for the uploaded file.
 * @param defaultDocumentName File name to use if fileName is not a valid
 * Microsoft Graph file name.
 * @param fileContent Content to upload.
 * @param parentFolder A OneDriveItem representing a Folder on OneDrive.  If
 * provided, the file will be uploaded into the folder - defaults to the Root of
 * OneDrive.
 * @returns A Promise of the resulting OneDriveItem representing the uploaded file.
 */
export const saveDocumentToOneDrive = async (
    graphClient: Client,
    fileName: string,
    defaultDocumentName: string,
    fileContent: Blob,
    parentFolder?: OneDriveItem,
): Promise<OneDriveItem> => {
    let sanitizedFilename = safeMicrosoftFileName(
        fileName,
        defaultDocumentName,
    );

    // Define the destination path in OneDrive
    const parentId = parentFolder ? parentFolder.id : ONE_DRIVE_ROOT_FOLDER;
    const parentPath = `${USER_ONE_DRIVE_BASE}/items/${parentId}`;

    // list oneDrive items in parent folder
    const existingFilenames = await listChildrenOfDriveItem(
        graphClient,
        parentPath,
    ).then((existingFiles) => existingFiles.map((file) => file.name));

    sanitizedFilename = uniqueFilename(existingFilenames, sanitizedFilename);

    const uploadPath = `${parentPath}:/${sanitizedFilename}:/content`;

    // Upload the file to OneDrive
    return graphClient
        .api(uploadPath)
        .put(fileContent)
        .then(assertResponseIsOneDriveItem);
};

export function uniqueFilename(
    existingFilenames: string[],
    desiredFilename: string,
): string {
    // Check if the desired filename already exists
    if (!existingFilenames.includes(desiredFilename)) {
        return desiredFilename;
    }

    const extensionIndex = desiredFilename.indexOf(".");
    const baseName =
        extensionIndex !== -1
            ? desiredFilename.substring(0, extensionIndex)
            : desiredFilename;
    const extension =
        extensionIndex !== -1 ? desiredFilename.substring(extensionIndex) : "";

    let counter = 1;
    let uniqueFilename = `${baseName} (${counter})${extension}`;

    // Keep incrementing the counter until a unique name is found
    while (existingFilenames.includes(uniqueFilename)) {
        counter++;
        uniqueFilename = `${baseName} (${counter})${extension}`;
    }

    return uniqueFilename;
}

export const listChildrenOfDriveItem = (
    client: Client,
    driveItemPath: string,
): Promise<OneDriveItem[]> => {
    return client
        .api(`${driveItemPath}/children`)
        .get()
        .then((response) => {
            return assertResponseIsListChildrenResponse(response).value;
        });
};

/**
 * Gets a Folder with the provided name from Microsoft OneDrive.
 *
 * @param client Microsoft Graph Client that performs the request.
 * @param folderName Name of the folder to get.
 * @returns A Promise of a OneDriveItem representing the Folder or `null` if a
 * folder of that name is not found.
 */
export const getFolderByName = (
    client: Client,
    folderName: string,
): Promise<OneDriveItem | null> => {
    const folderPath = `${USER_ONE_DRIVE_BASE}/${ONE_DRIVE_ROOT_FOLDER}:/${folderName}`;

    return client
        .api(folderPath)
        .get()
        .then(assertResponseIsOneDriveItem)
        .catch((e) => {
            // check if the error is because the file does not exist
            if (isGraphAPIError(e) && e.statusCode === 404) {
                return null;
            }
            if (isGraphAPIError(e) && e.statusCode === 400) {
                if (isMissingLicenseError(e)) {
                    throw new KAError(MISSING_LICENSE_MESSAGE, Errors.Service, {
                        cause: e.message,
                    });
                }
            }
            throw new KAError(
                "Unexpected error from Microsoft",
                Errors.Service,
                {cause: e},
            );
        });
};

/**
 * Gets a OneDrive Folder by name if it exists, otherwise creates a folder of that name.
 *
 * @param client Microsoft Graph Client that performs the request.
 * @param folderName The name of the desired folder.
 * @returns A Promise of a OneDriveItem representing the Folder.
 */
export const getOrCreateFolder = async (
    client: Client,
    folderName: string,
): Promise<OneDriveItem> => {
    const existingFolder = await getFolderByName(client, folderName);

    if (existingFolder !== null) {
        return existingFolder;
    }

    const driveItem = {
        name: folderName,
        folder: {},
        "@microsoft.graph.conflictBehavior": "rename", // rename the new folder and try again
    };

    return client
        .api(`${USER_ONE_DRIVE_BASE}/${ONE_DRIVE_ROOT_FOLDER}/children`)
        .post(driveItem)
        .then(assertResponseIsOneDriveItem);
};

/**
 * Verifies the type of response is OneDriveItem and throws a KAError otherwise.
 *
 * @param response the value to check the type of.
 * @returns a OneDriveItem if response is a OneDriveItem.  Otherwise throws a
 * KAError of type 'Service'
 */
function assertResponseIsOneDriveItem(response: any): OneDriveItem {
    if (!isOneDriveItem(response)) {
        throw new KAError(
            `response was not a OneDriveItem: ${JSON.stringify(response)}`,
            Errors.Service,
        );
    }
    return response;
}

/**
 * Verifies the type of response is a ListChildrenResponse and throws a KAError
 * otherwise.
 *
 * @param response the value to check the type of.
 * @returns response as a ListChildrenResponse if it is a ListChildrenResponse.
 * Otherwise throws a KAError of type 'Service'
 */
function assertResponseIsListChildrenResponse(
    response: any,
): ListChildrenResponse {
    if (!isListChildrenResponse(response)) {
        throw new KAError(
            `response was not a ListChildrenResponse: ${JSON.stringify(response)}`,
            Errors.Service,
        );
    }
    return response;
}
